import React, { useState, useEffect } from "react";
import {
  Trash2,
  X,
  ChevronLeft,
  ChevronRight,
  Menu,
  Calendar,
  Loader2,
} from "lucide-react";
import {
  getRbd,
  createRbd,
  updateRbd,
  deleteRbd,
  toggleSeoPin,
} from "../../service/rbdManagement/rbdService";
import { RbdManagementData } from "../../types/types";

// Interface for new rule form
interface NewRule {
  departure: string;
  destination: string;
  airLine: string;
  rbd1: string;
  rbd2: string;
  adultPrice?: string;
  childPrice?: string;
  infantPrice?: string;
  outboundAdultBaggage: string;
  outboundChildBaggage: string;
  outboundInfantBaggage: string;
  inboundAdultBaggage: string;
  inboundChildBaggage: string;
  inboundInfantBaggage: string;
  baggageInKg: boolean;
  oneWay: boolean;
  flexible: boolean;
  outboundValidity: string;
  inboundValidity: string;
  administratingCompany: string;
}

interface PricingData {
  departureSurgeDays: string[];
  arrivalSurgeDays: string[];
  departureSurgePrices: { adult: number; child: number; infant: number };
  arrivalSurgePrices: { adult: number; child: number; infant: number };
}

const checkForDuplicateRbds = (
  newRule: NewRule,
  rbdList: RbdManagementData[]
): boolean => {
  console.log("=== DUPLICATE CHECK DEBUG ===");
  console.log("New Rule:", {
    departure: newRule.departure,
    destination: newRule.destination,
    airLine: newRule.airLine,
    rbd1: newRule.rbd1,
    rbd2: newRule.rbd2,
    oneWay: newRule.oneWay,
    outboundValidity: newRule.outboundValidity,
    inboundValidity: newRule.inboundValidity,
  });

  const existingRbds = rbdList;

  const isDuplicate = existingRbds.some((existingRbd) => {
    // 1. Check same journey
    const sameJourney =
      existingRbd.departure.toUpperCase() === newRule.departure.toUpperCase() &&
      existingRbd.destination.toUpperCase() ===
        newRule.destination.toUpperCase();
    if (!sameJourney) return false;

    // 2. Check same airline
    const sameAirline =
      existingRbd.airLine.toUpperCase() === newRule.airLine.toUpperCase();
    if (!sameAirline) return false;

    // 3. Check same trip type
    const sameTripType = existingRbd.isOneWay === newRule.oneWay;
    if (!sameTripType) return false;

    // 4. Check RBD combination
    const normalizeRbdValue = (value: string | null | undefined): string => {
      if (
        value === null ||
        value === undefined ||
        value === "" ||
        value === "-"
      ) {
        return "";
      }
      return value.toUpperCase().trim();
    };

    const existingRbd1 = normalizeRbdValue(existingRbd.rbd1);
    const existingRbd2 = normalizeRbdValue(existingRbd.rbd2);
    const newRbd1 = normalizeRbdValue(newRule.rbd1);
    const newRbd2 = normalizeRbdValue(newRule.rbd2);

    let sameRbdCombination = false;
    if (newRule.oneWay) {
      const existingRbdValue =
        existingRbd1 !== "" ? existingRbd1 : existingRbd2;
      const newRbdValue = newRbd1 !== "" ? newRbd1 : newRbd2;
      const existingHasOneRbd =
        (existingRbd1 !== "" && existingRbd2 === "") ||
        (existingRbd1 === "" && existingRbd2 !== "");
      const newHasOneRbd =
        (newRbd1 !== "" && newRbd2 === "") ||
        (newRbd1 === "" && newRbd2 !== "");

      if (existingHasOneRbd && newHasOneRbd) {
        sameRbdCombination = existingRbdValue === newRbdValue;
      }
    } else {
      sameRbdCombination =
        (existingRbd1 === newRbd1 && existingRbd2 === newRbd2) ||
        (existingRbd1 === newRbd2 && existingRbd2 === newRbd1);
    }
    if (!sameRbdCombination) return false;

    // 5. UPDATED: Check validity period string match
    const existingOut =
      existingRbd.outboundValidity || existingRbd.timeRange || "";
    const existingIn = existingRbd.inboundValidity || "";

    const sameOutbound = existingOut === newRule.outboundValidity;

    if (newRule.oneWay) {
      return sameOutbound;
    } else {
      const sameInbound = existingIn === newRule.inboundValidity;
      return sameOutbound && sameInbound;
    }
  });

  return isDuplicate;
};

// Custom Calendar component
interface CustomCalendarProps {
  onDateRangeSelect: (startDate: Date, endDate: Date) => void;
  onClose: () => void;
}

const CustomCalendar: React.FC<CustomCalendarProps> = ({
  onDateRangeSelect,
  onClose,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isSelectingRange, setIsSelectingRange] = useState(false);
  const [showYearSelector, setShowYearSelector] = useState(false);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const daysOfWeek = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const endYear = 2030;
    const years = [];
    for (let year = currentYear; year <= endYear; year++) {
      years.push(year);
    }
    return years;
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7;

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const isDateDisabled = (date: Date): boolean => {
    const dateToCheck = new Date(date);
    dateToCheck.setHours(0, 0, 0, 0);
    return dateToCheck < today;
  };

  const isDateInRange = (date: Date) => {
    if (!startDate || !endDate) return false;
    return date >= startDate && date <= endDate;
  };

  const isDateSelected = (date: Date) => {
    if (!startDate) return false;
    if (!endDate) return date.getTime() === startDate.getTime();
    return (
      date.getTime() === startDate.getTime() ||
      date.getTime() === endDate.getTime()
    );
  };

  const handleDateClick = (date: Date) => {
    if (isDateDisabled(date)) {
      return;
    }

    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null);
      setIsSelectingRange(true);
    } else if (startDate && !endDate) {
      if (date >= startDate) {
        setEndDate(date);
        setIsSelectingRange(false);
        onDateRangeSelect(startDate, date);
      } else {
        setStartDate(date);
        setEndDate(null);
      }
    }
  };

  const navigateMonth = (direction: "prev" | "next") => {
    const newDate = new Date(currentDate);
    if (direction === "prev") {
      newDate.setMonth(currentDate.getMonth() - 1);
    } else {
      newDate.setMonth(currentDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const handleYearChange = (year: number) => {
    const newDate = new Date(currentDate);
    newDate.setFullYear(year);
    setCurrentDate(newDate);
    setShowYearSelector(false);
  };

  const isPrevMonthDisabled = (): boolean => {
    const prevMonth = new Date(currentDate);
    prevMonth.setMonth(currentDate.getMonth() - 1);
    prevMonth.setDate(1);

    const lastDayOfPrevMonth = new Date(
      prevMonth.getFullYear(),
      prevMonth.getMonth() + 1,
      0
    );
    return lastDayOfPrevMonth < today;
  };

  const days = getDaysInMonth(currentDate);
  const yearOptions = generateYearOptions();

  return (
    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-2 z-50 w-full max-w-[280px]">
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={() => navigateMonth("prev")}
          disabled={isPrevMonthDisabled()}
          className={`p-0.5 rounded ${
            isPrevMonthDisabled()
              ? "text-gray-300 cursor-not-allowed"
              : "hover:bg-gray-100 text-gray-600"
          }`}
        >
          <ChevronLeft size={12} />
        </button>

        <div className="flex flex-col items-center">
          <h3 className="font-medium text-xs">
            {monthNames[currentDate.getMonth()]}
          </h3>

          <div className="relative">
            <button
              onClick={() => setShowYearSelector(!showYearSelector)}
              className="text-xs text-blue-600 hover:text-blue-800 underline"
            >
              {currentDate.getFullYear()}
            </button>

            {showYearSelector && (
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 bg-white border border-gray-200 rounded shadow-lg z-60 min-w-[80px]">
                {yearOptions.map((year) => (
                  <button
                    key={year}
                    onClick={() => handleYearChange(year)}
                    className={`block w-full px-3 py-1 text-xs text-left hover:bg-gray-100 ${
                      year === currentDate.getFullYear()
                        ? "bg-blue-100 text-blue-700 font-medium"
                        : "text-gray-700"
                    }`}
                  >
                    {year}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <button
          onClick={() => navigateMonth("next")}
          className="p-0.5 hover:bg-gray-100 rounded text-gray-600"
        >
          <ChevronRight size={12} />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {daysOfWeek.map((day) => (
          <div
            key={day}
            className="text-center text-[10px] font-medium text-gray-600 py-0.5"
          >
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {days.map((date, index) => (
          <div key={index} className="h-5 flex items-center justify-center">
            {date ? (
              <button
                onClick={() => handleDateClick(date)}
                disabled={isDateDisabled(date)}
                className={`w-5 h-5 text-[10px] rounded-sm flex items-center justify-center transition-colors ${
                  isDateDisabled(date)
                    ? "text-gray-300 cursor-not-allowed bg-gray-50"
                    : isDateSelected(date)
                    ? "bg-blue-500 text-white"
                    : isDateInRange(date)
                    ? "bg-blue-100 text-blue-700"
                    : "hover:bg-gray-100 text-gray-700"
                }`}
                title={
                  isDateDisabled(date) ? "Past dates cannot be selected" : ""
                }
              >
                {date.getDate()}
              </button>
            ) : (
              <div className="w-5 h-5"></div>
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-end mt-2">
        <button
          onClick={onClose}
          className="px-1 py-0.5 text-[10px] text-gray-600 hover:text-gray-800"
        >
          Close
        </button>
      </div>
    </div>
  );
};

interface PriceWithTooltipProps {
  basePrice: number;
  depSurge: number;
  arrSurge: number;
  departure: string;
}

const PriceWithTooltip: React.FC<PriceWithTooltipProps> = ({
  basePrice,
  depSurge,
  arrSurge,
  departure,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const currency = departure.toUpperCase() === "CMB" ? "LKR" : "EUR";

  const formatPrice = (price: number) => {
    return price.toLocaleString("en-US");
  };

  const total = basePrice + depSurge + arrSurge;

  return (
    <div
      className="font-mono w-full relative"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div className="text-sm font-medium text-gray-900 text-right">
        {currency} {formatPrice(basePrice)}
      </div>
      {depSurge > 0 && (
        <div className="text-xs text-green-600 text-right">
          + {currency} {formatPrice(depSurge)}
        </div>
      )}
      {arrSurge > 0 && (
        <div className="text-xs text-purple-600 text-right">
          + {currency} {formatPrice(arrSurge)}
        </div>
      )}

      {/* Tooltip */}
      {showTooltip && (depSurge > 0 || arrSurge > 0) && (
        <div className="absolute bottom-full left-0 mb-2 p-2 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap z-10">
          {currency} {formatPrice(basePrice)} + {currency}{" "}
          {formatPrice(depSurge)} (dep) + {currency} {formatPrice(arrSurge)}{" "}
          (arr) = {currency} {formatPrice(total)}
        </div>
      )}
    </div>
  );
};

const RBDManagement = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [rbdList, setRbdList] = useState<RbdManagementData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showPricingModal, setShowPricingModal] = useState<boolean>(false);
  const [showEditPricesModal, setShowEditPricesModal] = useState<boolean>(false);
  const [showDuplicatePopup, setShowDuplicatePopup] = useState<boolean>(false);
  const [currentRule, setCurrentRule] = useState<RbdManagementData | null>(
    null
  );
  const [error, setError] = useState<string>("");
  const [pinningId, setPinningId] = useState<number | null>(null);
  const [success, setSuccess] = useState<string>("");
  const [showDeleteConfirmation, setShowDeleteConfirmation] =
    useState<boolean>(false);
  const [ruleToDelete, setRuleToDelete] = useState<number | null>(null);
  const [newlyAddedRuleId, setNewlyAddedRuleId] = useState<number | null>(null);

  const [newRule, setNewRule] = useState<NewRule>({
    departure: "",
    destination: "",
    airLine: "",
    rbd1: "",
    rbd2: "",
    adultPrice: "",
    childPrice: "",
    infantPrice: "",
    outboundAdultBaggage: "",
    outboundChildBaggage: "",
    outboundInfantBaggage: "",
    inboundAdultBaggage: "",
    inboundChildBaggage: "",
    inboundInfantBaggage: "",
    oneWay: false,
    flexible: false,
    baggageInKg: false,
    outboundValidity: "",
    inboundValidity: "",
    administratingCompany: "",
  });

  const checkForDuplicates = (): boolean => {
    return checkForDuplicateRbds(newRule, rbdList);
  };

  // Load RBD data on component mount
  useEffect(() => {
    loadRbdData();
  }, []);

  const loadRbdData = async () => {
    try {
      setLoading(true);
      const data = await getRbd();
      setRbdList(data);
      setError("");
    } catch (error) {
      setError("Failed to load RBD data");
      console.error("Error loading RBD data:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderBaggageInfo = (
    baggage: string | undefined,
    isKgBaggage: boolean | undefined
  ) => {
    if (!baggage || baggage.trim() === "") {
      return <span className="text-gray-400 text-xs italic">-</span>;
    }

    // Extract just the number from the stored value
    const numericValue = baggage.replace(/[^\d]/g, "");

    return (
      <span className="text-xs font-medium text-gray-900">
        {numericValue}
        {isKgBaggage ? "Kg" : "Pc"}
      </span>
    );
  };

  const renderBaggageStack = (
    adult?: string,
    child?: string,
    infant?: string,
    isKgBaggage?: boolean
  ) => {
    const allEmpty =
      (!adult || !adult.trim()) &&
      (!child || !child.trim()) &&
      (!infant || !infant.trim());

    if (allEmpty)
      return <span className="text-gray-400 text-xs italic">-</span>;

    return (
      <div className="grid grid-cols-[20px,1fr] items-baseline gap-x-2 gap-y-1 text-xs leading-5">
        <span className="text-gray-600 text-right w-[20px]">A:</span>
        <span className="font-medium">
          {renderBaggageInfo(adult, isKgBaggage)}
        </span>

        <span className="text-gray-600 text-right w-[20px]">C:</span>
        <span className="font-medium">
          {renderBaggageInfo(child, isKgBaggage)}
        </span>

        <span className="text-gray-600 text-right w-[20px]">I:</span>
        <span className="font-medium">
          {renderBaggageInfo(infant, isKgBaggage)}
        </span>
      </div>
    );
  };

  const showMessage = (message: string, type: "success" | "error") => {
    if (type === "success") {
      setSuccess(message);
      setError("");
      setTimeout(() => setSuccess(""), 3000);
    } else {
      setError(message);
      setSuccess("");
      setTimeout(() => setError(""), 5000);
    }
  };

  // Group RBDs by route
  const groupedRbds: Record<string, RbdManagementData[]> = rbdList.reduce(
    (acc, rbd) => {
      const routeKey = `${rbd.departure} to ${rbd.destination}`;
      if (!acc[routeKey]) {
        acc[routeKey] = [];
      }
      acc[routeKey].push(rbd);
      return acc;
    },
    {} as Record<string, RbdManagementData[]>
  );

  // Sort and organize each route's RBDs
  Object.keys(groupedRbds).forEach((routeKey) => {
    const rbds = groupedRbds[routeKey];

    // Step 1: Group by airline within this route
    const airlineGroups: Record<string, RbdManagementData[]> = {};

    rbds.forEach((rbd) => {
      const airline = rbd.airLine;
      if (!airlineGroups[airline]) {
        airlineGroups[airline] = [];
      }
      airlineGroups[airline].push(rbd);
    });

    // Step 2: Process each airline group
    const finalSortedRbds: RbdManagementData[] = [];

    const sortedAirlines = Object.keys(airlineGroups).sort((a, b) =>
      a.localeCompare(b)
    );

    sortedAirlines.forEach((airline) => {
      const airlineRbds = airlineGroups[airline];

      // Separate round trips and one ways for this airline
      const roundTrips = airlineRbds.filter((rbd) => !rbd.isOneWay);
      const oneWays = airlineRbds.filter((rbd) => rbd.isOneWay);

      // Sort round trips by adult price (lowest first)
      const sortedRoundTrips = roundTrips.sort((a, b) => {
        const priceA = Number(a.adultPrice) || 0;
        const priceB = Number(b.adultPrice) || 0;
        return priceA - priceB;
      });

      // Sort one ways by adult price (lowest first)
      const sortedOneWays = oneWays.sort((a, b) => {
        const priceA = Number(a.adultPrice) || 0;
        const priceB = Number(b.adultPrice) || 0;
        return priceA - priceB;
      });

      // Add this airline's round trips FIRST, then one ways
      finalSortedRbds.push(...sortedRoundTrips, ...sortedOneWays);
    });

    groupedRbds[routeKey] = finalSortedRbds;
  });

  const handleAddRule = () => {
    setShowAddModal(true);
  };

  const handleAddRuleSubmit = async () => {
    if (checkForDuplicates()) {
      setShowDuplicatePopup(true);
      return;
    }

    const newRbdRule: Partial<RbdManagementData> = {
      ...newRule,
      isOneWay: newRule.oneWay,
      isKgBaggage: newRule.baggageInKg,
      isFlexible: newRule.flexible,
      adultPrice: newRule.adultPrice || "0",
      childPrice: newRule.childPrice || "0",
      infantPrice: newRule.infantPrice || "0",
      // UPDATED: Send split validity
      outboundValidity: newRule.outboundValidity,
      inboundValidity: newRule.oneWay ? undefined : newRule.inboundValidity,
      // Fallback/Backwards compatibility
      timeRange: newRule.outboundValidity,
      outboundAdultBaggage: newRule.outboundAdultBaggage,
      outboundChildBaggage: newRule.outboundChildBaggage,
      outboundInfantBaggage: newRule.outboundInfantBaggage,
      inboundAdultBaggage: newRule.inboundAdultBaggage,
      inboundChildBaggage: newRule.inboundChildBaggage,
      inboundInfantBaggage: newRule.inboundInfantBaggage,
      departureSurgeDays: [],
      arrivalSurgeDays: [],
      departureSurgeAdultPrice: "0",
      departureSurgeChildPrice: "0",
      departureSurgeInfantPrice: "0",
      arrivalSurgeAdultPrice: "0",
      arrivalSurgeChildPrice: "0",
      arrivalSurgeInfantPrice: "0",
    };

    try {
      const result = await createRbd(newRbdRule);
      if (result.success) {
        showMessage("RBD rule created successfully!", "success");

        if (result.data && result.data.id) {
          setNewlyAddedRuleId(result.data.id);

          setTimeout(() => {
            setNewlyAddedRuleId(null);
          }, 5000);
        }

        await loadRbdData();
        setShowAddModal(false);
        resetNewRule();
      } else {
        if (
          !result.message.toLowerCase().includes("already exists") &&
          !result.message.toLowerCase().includes("duplicate")
        ) {
          showMessage(result.message, "error");
        }
      }
    } catch (error) {
      showMessage("Failed to create RBD rule", "error");
    }
  };

  const handleSurgePricingRule = (rbd: RbdManagementData) => {
    setCurrentRule(rbd);
    setShowPricingModal(true);
  };

  
  const handleToggleSeoPin = async (id: number) => {
    setPinningId(id);
    try {
      await toggleSeoPin(id);
      setRbdList((prev) => 
        prev.map((r) => r.id === id ? { ...r, isSeoPinned: !r.isSeoPinned } : r)
      );
    } catch (e) {
      console.error(e);
      alert("Failed to toggle SEO Pin");
    }
    setPinningId(null);
  };

  const handleEditPrices = (rbd: RbdManagementData) => {
    setCurrentRule(rbd);
    setShowEditPricesModal(true);
  };

  const handleUpdateSurgeRule = async (pricingData: PricingData) => {
    if (!currentRule || !currentRule.id) return;

    const updatedRule: Partial<RbdManagementData> = {
      ...currentRule,
      departureSurgeDays: pricingData.departureSurgeDays,
      arrivalSurgeDays: pricingData.arrivalSurgeDays,
      departureSurgeAdultPrice:
        pricingData.departureSurgePrices.adult.toString(),
      departureSurgeChildPrice:
        pricingData.departureSurgePrices.child.toString(),
      departureSurgeInfantPrice:
        pricingData.departureSurgePrices.infant.toString(),
      arrivalSurgeAdultPrice: pricingData.arrivalSurgePrices.adult.toString(),
      arrivalSurgeChildPrice: pricingData.arrivalSurgePrices.child.toString(),
      arrivalSurgeInfantPrice: pricingData.arrivalSurgePrices.infant.toString(),
    };

    try {
      const result = await updateRbd(currentRule.id, updatedRule);
      if (result.success) {
        showMessage("Surge pricing updated successfully!", "success");
        await loadRbdData();
        setShowPricingModal(false);
        setCurrentRule(null);
      } else {
        showMessage(result.message, "error");
      }
    } catch (error) {
      showMessage("Failed to update surge pricing", "error");
    }
  };

  const handleDeleteRule = async (id: number) => {
    setRuleToDelete(id);
    setShowDeleteConfirmation(true);
  };

  const confirmDelete = async () => {
    if (!ruleToDelete) return;

    try {
      const result = await deleteRbd(ruleToDelete);
      if (result.success) {
        showMessage("RBD rule deleted successfully!", "success");
        await loadRbdData();
      } else {
        showMessage(result.message, "error");
      }
    } catch (error) {
      showMessage("Failed to delete RBD rule", "error");
    } finally {
      setShowDeleteConfirmation(false);
      setRuleToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirmation(false);
    setRuleToDelete(null);
  };

  const resetNewRule = () => {
    setNewRule({
      departure: "",
      destination: "",
      airLine: "",
      rbd1: "",
      rbd2: "",
      adultPrice: "",
      childPrice: "",
      infantPrice: "",
      outboundAdultBaggage: "",
      outboundChildBaggage: "",
      outboundInfantBaggage: "",
      inboundAdultBaggage: "",
      inboundChildBaggage: "",
      inboundInfantBaggage: "",
      oneWay: false,
      flexible: false,
      baggageInKg: false,
      outboundValidity: "",
      inboundValidity: "",
      administratingCompany: "",
    });
  };

  const hasSurgePricing = (rbd: RbdManagementData) => {
    const hasDepartureSurge =
      (rbd.departureSurgeDays && rbd.departureSurgeDays.length > 0) ||
      Number(rbd.departureSurgeAdultPrice || 0) > 0 ||
      Number(rbd.departureSurgeChildPrice || 0) > 0 ||
      Number(rbd.departureSurgeInfantPrice || 0) > 0;

    if (rbd.isOneWay) {
      return hasDepartureSurge;
    }

    const hasArrivalSurge =
      (rbd.arrivalSurgeDays && rbd.arrivalSurgeDays.length > 0) ||
      Number(rbd.arrivalSurgeAdultPrice || 0) > 0 ||
      Number(rbd.arrivalSurgeChildPrice || 0) > 0 ||
      Number(rbd.arrivalSurgeInfantPrice || 0) > 0;

    return hasDepartureSurge || hasArrivalSurge;
  };

  const renderSurgeDays = (departureDays: string[], arrivalDays: string[]) => {
    return (
      <div className="flex flex-col gap-1">
        {departureDays && departureDays.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {departureDays.map((day, index) => (
              <span
                key={`dep-${day}`}
                className="text-green-600 font-medium text-xs"
              >
                {day}
                {index < departureDays.length - 1 ? "," : ""}
              </span>
            ))}
          </div>
        )}
        {arrivalDays && arrivalDays.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {arrivalDays.map((day, index) => (
              <span
                key={`arr-${day}`}
                className="text-purple-600 font-medium text-xs"
              >
                {day}
                {index < arrivalDays.length - 1 ? "," : ""}
              </span>
            ))}
          </div>
        )}
        {(!departureDays || departureDays.length === 0) &&
          (!arrivalDays || arrivalDays.length === 0) && (
            <span className="text-gray-400 text-xs italic">Not configured</span>
          )}
      </div>
    );
  };

  const DuplicatePopup: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const handleClose = () => {
      setShowDuplicatePopup(false);
      onClose();
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-yellow-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Duplicate RBD Rule Detected
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                A rule with the same airline ({newRule.airLine}), route (
                {newRule.departure} to {newRule.destination}), trip type (
                {newRule.oneWay ? "One Way" : "Round Trip"}), and RBD
                combination ({newRule.rbd1}
                {newRule.rbd2 ? `-${newRule.rbd2}` : ""}) already exists. Please
                review the existing rules or modify your input.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 bg-gray-50 min-h-screen">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-gray-600">Loading RBD data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center">
          <h1 className="text-xl font-bold text-blue-600">AirConnect</h1>
          <span className="ml-1 text-sm text-gray-600">Admin</span>
        </div>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded-md text-gray-600 hover:bg-gray-100"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 w-80 bg-white z-50 lg:hidden overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center">
                  <h1 className="text-2xl font-bold text-blue-600">
                    AirConnect
                  </h1>
                  <span className="ml-1 text-lg text-gray-600">Admin</span>
                </div>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 rounded-md text-gray-600 hover:bg-gray-100"
                >
                  <X size={20} />
                </button>
              </div>
              <nav className="space-y-2">
                <div className="w-full flex items-center px-3 py-3 text-left rounded-lg text-gray-700 hover:bg-gray-50">
                  <span className="text-sm font-medium">Dashboard</span>
                </div>
                <div className="w-full flex items-center px-3 py-3 text-left rounded-lg text-gray-700 hover:bg-gray-50">
                  <span className="text-sm font-medium">Users</span>
                </div>
                <div className="w-full flex items-center px-3 py-3 text-left rounded-lg text-gray-700 hover:bg-gray-50">
                  <span className="text-sm font-medium">Bookings</span>
                </div>
                <div className="w-full flex items-center px-3 py-3 text-left rounded-lg text-gray-700 hover:bg-gray-50">
                  <span className="text-sm font-medium">
                    Content Management
                  </span>
                </div>
                <div className="w-full flex items-center px-3 py-3 text-left rounded-lg text-gray-700 hover:bg-gray-50">
                  <span className="text-sm font-medium">Negotiate Price</span>
                </div>
                <div className="w-full flex items-center px-3 py-3 text-left rounded-lg text-gray-700 hover:bg-gray-50">
                  <span className="text-sm font-medium">Commissions</span>
                </div>
                <div className="w-full flex items-center px-3 py-3 text-left rounded-lg text-gray-700 hover:bg-gray-50">
                  <span className="text-sm font-medium">Luggages</span>
                </div>
                <div className="w-full flex items-center px-3 py-3 text-left rounded-lg text-gray-700 hover:bg-gray-50">
                  <span className="text-sm font-medium">Coupons</span>
                </div>
                <div className="w-full flex items-center px-3 py-3 text-left rounded-lg bg-blue-50 text-blue-700">
                  <span className="text-sm font-medium">RBD Management</span>
                </div>
                <div className="w-full flex items-center px-3 py-3 text-left rounded-lg text-gray-700 hover:bg-gray-50">
                  <span className="text-sm font-medium">Settings</span>
                </div>
              </nav>
            </div>
          </div>
        </>
      )}

      <div className="flex-1 p-4 lg:p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl lg:text-3xl font-bold text-gray-800">
            RBD Settings
          </h2>
          <button
            onClick={handleAddRule}
            className="bg-blue-600 text-white px-4 py-2 lg:px-6 lg:py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm lg:text-base flex items-center gap-2"
          >
            Add RBD Rule
          </button>
        </div>

        {success && (
          <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
            {success}
          </div>
        )}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* RBD Rules Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1200px]">
              <thead className="bg-gray-100 border-b">
                <tr>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Airline
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dept
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dest
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    RBD 1
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    RBD 2
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Time Period
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Surge Days
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Adult Price
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Child Price
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Infant Price
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Out Bag
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    In Bag
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(groupedRbds).map(([route, rbds]) => {
                  const roundTripCount = rbds.filter(
                    (rbd) => !rbd.isOneWay
                  ).length;

                  return (
                    <React.Fragment key={route}>
                      <tr style={{ backgroundColor: "#E8E8E8" }}>
                        <td
                          colSpan={13}
                          className="px-2 py-2 font-semibold text-gray-700"
                        >
                          {route}
                        </td>
                      </tr>
                      {rbds.map((rbd, index) => {
                        return (
                          <React.Fragment key={rbd.id}>
                            <tr
                              className="hover:bg-gray-50"
                              style={{
                                backgroundColor:
                                  newlyAddedRuleId === rbd.id
                                    ? "#dcfce7"
                                    : index % 2 === 0
                                    ? "white"
                                    : "#E9F0FF",
                                transition: "background-color 0.3s ease",
                              }}
                            >
                              <td className="px-2 py-3 text-sm font-medium text-gray-900">
                                <div>
                                  <span>{rbd.airLine}</span>
                                  {rbd.administratingCompany && (
                                    <div className="text-xs text-gray-500 mt-1">
                                      Op. by: {rbd.administratingCompany}
                                    </div>
                                  )}
                                  <div
                                    className={`text-[10px] mt-1 font-bold ${
                                      rbd.isFlexible
                                        ? "text-green-600"
                                        : "text-gray-500"
                                    }`}
                                  >
                                    {rbd.isFlexible
                                      ? "Flexible: ON"
                                      : "Flexible: OFF"}
                                  </div>
                                </div>
                              </td>
                              <td className="px-2 py-3 text-sm text-gray-500">
                                {rbd.departure}
                              </td>
                              <td className="px-2 py-3 text-sm text-gray-500">
                                {rbd.destination}
                              </td>
                              <td className="px-2 py-3 text-sm text-gray-500">
                                {rbd.rbd1}
                              </td>
                              <td className="px-2 py-3 text-sm text-gray-500">
                                {rbd.rbd2 || "-"}
                              </td>

                              {/* UPDATED TIME PERIOD COLUMN */}
                              <td className="px-3 py-3 text-sm text-gray-500 whitespace-nowrap">
                                <div className="flex flex-col gap-1">
                                  {/* Outbound */}
                                  <div className="flex items-center gap-1">
                                    <span className="text-[10px] font-bold text-blue-600 uppercase w-6">
                                      Out:
                                    </span>
                                    <span className="text-xs">
                                      {rbd.outboundValidity || rbd.timeRange}
                                    </span>
                                  </div>

                                  {/* Inbound - Only show if not one-way */}
                                  {!rbd.isOneWay && (
                                    <div className="flex items-center gap-1">
                                      <span className="text-[10px] font-bold text-purple-600 uppercase w-6">
                                        In:
                                      </span>
                                      <span className="text-xs">
                                        {rbd.inboundValidity || "-"}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </td>

                              <td className="px-2 py-3 text-sm">
                                {renderSurgeDays(
                                  rbd.departureSurgeDays || [],
                                  rbd.arrivalSurgeDays || []
                                )}
                              </td>
                              <td className="px-2 py-3 text-sm">
                                <PriceWithTooltip
                                  basePrice={Number(rbd.adultPrice)}
                                  depSurge={Number(
                                    rbd.departureSurgeAdultPrice || 0
                                  )}
                                  arrSurge={Number(
                                    rbd.arrivalSurgeAdultPrice || 0
                                  )}
                                  departure={rbd.departure}
                                />
                              </td>
                              <td className="px-2 py-3 text-sm">
                                <PriceWithTooltip
                                  basePrice={Number(rbd.childPrice)}
                                  depSurge={Number(
                                    rbd.departureSurgeChildPrice || 0
                                  )}
                                  arrSurge={Number(
                                    rbd.arrivalSurgeChildPrice || 0
                                  )}
                                  departure={rbd.departure}
                                />
                              </td>
                              <td className="px-2 py-3 text-sm">
                                <PriceWithTooltip
                                  basePrice={Number(rbd.infantPrice)}
                                  depSurge={Number(
                                    rbd.departureSurgeInfantPrice || 0
                                  )}
                                  arrSurge={Number(
                                    rbd.arrivalSurgeInfantPrice || 0
                                  )}
                                  departure={rbd.departure}
                                />
                              </td>
                              <td className="px-2 py-3 text-sm text-gray-500 align-top">
                                {renderBaggageStack(
                                  rbd.outboundAdultBaggage,
                                  rbd.outboundChildBaggage,
                                  rbd.outboundInfantBaggage,
                                  rbd.isKgBaggage
                                )}
                              </td>
                              <td className="px-2 py-3 text-sm text-gray-500 align-top">
                                {renderBaggageStack(
                                  rbd.inboundAdultBaggage,
                                  rbd.inboundChildBaggage,
                                  rbd.inboundInfantBaggage,
                                  rbd.isKgBaggage
                                )}
                              </td>

                              <td className="px-2 py-3 text-sm">
                                <div className="flex items-center gap-2">

                                  <button
                                    onClick={() => handleToggleSeoPin(rbd.id)}
                                    disabled={pinningId === rbd.id}
                                    title={rbd.isSeoPinned ? "Unpin from SEO" : "Pin to SEO"}
                                    className={`px-3 py-1 rounded text-xs font-medium disabled:opacity-50 flex items-center gap-1 transition-colors ${
                                      rbd.isSeoPinned 
                                        ? "bg-yellow-100 text-yellow-800 border border-yellow-300 hover:bg-yellow-200" 
                                        : "bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200"
                                    }`}
                                  >
                                    {pinningId === rbd.id ? "..." : (rbd.isSeoPinned ? "📌 Pinned" : "📌 Pin SEO")}
                                  </button>

                                  <button
                                    onClick={() => handleEditPrices(rbd)}
                                    className="px-3 py-1 bg-green-600 text-white hover:bg-green-700 rounded text-xs transition-colors"
                                  >
                                    Edit Prices
                                  </button>

                                  <button
                                    onClick={() => handleSurgePricingRule(rbd)}
                                    disabled={hasSurgePricing(rbd)}
                                    className={`px-3 py-1 rounded text-xs transition-colors ${
                                      hasSurgePricing(rbd)
                                        ? "bg-gray-400 text-gray-600 cursor-not-allowed opacity-50"
                                        : "bg-blue-600 text-white hover:bg-blue-700"
                                    }`}
                                  >
                                    {hasSurgePricing(rbd) ? "Added" : "Add"}
                                  </button>

                                  <button
                                    onClick={() => handleDeleteRule(rbd.id)}
                                    className="text-red-600 hover:text-red-800 p-1"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add RBD Rule Modal */}
        {showAddModal && (
          <AddRBDRuleModal
            newRule={newRule}
            setNewRule={setNewRule}
            onSubmit={handleAddRuleSubmit}
            onClose={() => {
              setShowAddModal(false);
              resetNewRule();
            }}
            rbdList={rbdList}
            onDuplicateDetected={() => setShowDuplicatePopup(true)}
          />
        )}

        {/* Pricing Modal */}
        {showPricingModal && currentRule && (
          <PricingModal
            rule={currentRule}
            onSubmit={handleUpdateSurgeRule}
            onClose={() => {
              setShowPricingModal(false);
              setCurrentRule(null);
            }}
          />
        )}

        {/* Edit Prices Modal */}
        {showEditPricesModal && currentRule && (
          <EditPricesModal
            rule={currentRule}
            onClose={() => setShowEditPricesModal(false)}
            onSave={async (updatedPrices) => {
              try {
                await updateRbd(currentRule.id, updatedPrices);
                setShowEditPricesModal(false);
                loadRbdData(); // Refresh the list
              } catch (err) {
                console.error(err);
                alert("Failed to update prices");
              }
            }}
          />
        )}

        {showDuplicatePopup && (
          <DuplicatePopup
            onClose={() => {
              setShowDuplicatePopup(false);
            }}
          />
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 w-full max-w-md mx-4">
              <h3 className="text-xl font-semibold text-gray-900 text-center mb-8">
                Confirm the Delete Request
              </h3>

              <div className="flex gap-4 justify-center">
                <button
                  onClick={cancelDelete}
                  className="px-8 py-3 border-2 border-blue-500 text-blue-500 rounded-lg hover:bg-blue-50 transition-colors font-medium min-w-[120px]"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-8 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium min-w-[120px]"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Add RBD Rule Modal Component
interface AddRBDRuleModalProps {
  newRule: NewRule;
  setNewRule: React.Dispatch<React.SetStateAction<NewRule>>;
  onSubmit: () => void;
  onClose: () => void;
  rbdList: RbdManagementData[];
  onDuplicateDetected: () => void;
}

const AddRBDRuleModal: React.FC<AddRBDRuleModalProps> = ({
  newRule,
  setNewRule,
  onSubmit,
  onClose,
  rbdList,
  onDuplicateDetected,
}) => {
  // UPDATED: State to track WHICH validity field triggered the calendar
  const [activeCalendarField, setActiveCalendarField] = useState<
    "outbound" | "inbound" | null
  >(null);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currency = newRule.departure.toUpperCase() === "CMB" ? "LKR" : "EUR";

  const handleInputChange = (field: keyof NewRule, value: string | boolean) => {
    setNewRule((prev) => {
      const updated = { ...prev, [field]: value };

      if (field === "baggageInKg") {
        updated.outboundAdultBaggage = "";
        updated.outboundChildBaggage = "";
        updated.outboundInfantBaggage = "";
        updated.inboundAdultBaggage = "";
        updated.inboundChildBaggage = "";
        updated.inboundInfantBaggage = "";
      }

      return updated;
    });

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }

    if (field === "baggageInKg") {
      setErrors((prev) => ({
        ...prev,
        outboundBaggage: "",
        inboundBaggage: "",
      }));
    }
  };

  // UPDATED: Handle date selection based on active field
  const handleDateRangeSelect = (startDate: Date, endDate: Date) => {
    const startMonth = startDate.toLocaleDateString("en-US", {
      month: "short",
    });
    const endMonth = endDate.toLocaleDateString("en-US", { month: "short" });
    const startYear = startDate.getFullYear();
    const endYear = endDate.getFullYear();

    const dateString = `${startMonth} ${startDate.getDate()}, ${startYear} - ${endMonth} ${endDate.getDate()}, ${endYear}`;

    if (activeCalendarField === "outbound") {
      handleInputChange("outboundValidity", dateString);
      if (errors.outboundValidity)
        setErrors((prev) => ({ ...prev, outboundValidity: "" }));
    } else if (activeCalendarField === "inbound") {
      handleInputChange("inboundValidity", dateString);
      if (errors.inboundValidity)
        setErrors((prev) => ({ ...prev, inboundValidity: "" }));
    }

    setActiveCalendarField(null);
  };

  const validateBaggageFormat = (
    baggage: string,
    isKgBaggage: boolean
  ): boolean => {
    if (!baggage || baggage.trim() === "") return true;

    const trimmedBaggage = baggage.trim();
    const numberValue = parseFloat(trimmedBaggage);
    return !isNaN(numberValue) && numberValue >= 0;
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (
      newRule.outboundAdultBaggage &&
      newRule.outboundAdultBaggage.trim() !== ""
    ) {
      const isValidBaggage = validateBaggageFormat(
        newRule.outboundAdultBaggage,
        newRule.baggageInKg
      );
      if (!isValidBaggage) {
        newErrors.outboundAdultBaggage =
          "Outbound adult baggage must be a valid positive number";
      }
    }

    if (
      newRule.outboundChildBaggage &&
      newRule.outboundChildBaggage.trim() !== ""
    ) {
      const isValidBaggage = validateBaggageFormat(
        newRule.outboundChildBaggage,
        newRule.baggageInKg
      );
      if (!isValidBaggage) {
        newErrors.outboundChildBaggage =
          "Outbound child baggage must be a valid positive number";
      }
    }

    if (
      newRule.outboundInfantBaggage &&
      newRule.outboundInfantBaggage.trim() !== ""
    ) {
      const isValidBaggage = validateBaggageFormat(
        newRule.outboundInfantBaggage,
        newRule.baggageInKg
      );
      if (!isValidBaggage) {
        newErrors.outboundInfantBaggage =
          "Outbound infant baggage must be a valid positive number";
      }
    }

    if (!newRule.oneWay) {
      if (
        newRule.inboundAdultBaggage &&
        newRule.inboundAdultBaggage.trim() !== ""
      ) {
        const isValidBaggage = validateBaggageFormat(
          newRule.inboundAdultBaggage,
          newRule.baggageInKg
        );
        if (!isValidBaggage) {
          newErrors.inboundAdultBaggage =
            "Inbound adult baggage must be a valid positive number";
        }
      }

      if (
        newRule.inboundChildBaggage &&
        newRule.inboundChildBaggage.trim() !== ""
      ) {
        const isValidBaggage = validateBaggageFormat(
          newRule.inboundChildBaggage,
          newRule.baggageInKg
        );
        if (!isValidBaggage) {
          newErrors.inboundChildBaggage =
            "Inbound child baggage must be a valid positive number";
        }
      }

      if (
        newRule.inboundInfantBaggage &&
        newRule.inboundInfantBaggage.trim() !== ""
      ) {
        const isValidBaggage = validateBaggageFormat(
          newRule.inboundInfantBaggage,
          newRule.baggageInKg
        );
        if (!isValidBaggage) {
          newErrors.inboundInfantBaggage =
            "Inbound infant baggage must be a valid positive number";
        }
      }
    }

    if (!newRule.departure.trim()) {
      newErrors.departure = "Departure is required";
    } else if (newRule.departure.length !== 3) {
      newErrors.departure = "Departure must be exactly 3 characters";
    }

    // UPDATED: Validity validations
    if (!newRule.outboundValidity || newRule.outboundValidity.trim() === "") {
      newErrors.outboundValidity = "Outbound validity is required";
    }

    if (!newRule.oneWay) {
      if (!newRule.inboundValidity || newRule.inboundValidity.trim() === "") {
        newErrors.inboundValidity =
          "Inbound validity is required for round trips";
      }
    }

    if (!newRule.destination.trim()) {
      newErrors.destination = "Destination is required";
    } else if (newRule.destination.length !== 3) {
      newErrors.destination = "Destination must be exactly 3 characters";
    }

    if (!newRule.airLine.trim()) {
      newErrors.airLine = "Airline is required";
    } else if (newRule.airLine.length < 2 || newRule.airLine.length > 3) {
      newErrors.airLine = "Airline must be 2-3 characters";
    }

    if (!newRule.rbd1.trim()) {
      newErrors.rbd1 = "RBD 1 is required";
    } else if (newRule.rbd1.length !== 1) {
      newErrors.rbd1 = "RBD 1 must be exactly 1 character";
    }

    if (!newRule.oneWay && !newRule.rbd2.trim()) {
      newErrors.rbd2 = "RBD 2 is required for round trip";
    } else if (!newRule.oneWay && newRule.rbd2.length !== 1) {
      newErrors.rbd2 = "RBD 2 must be exactly 1 character";
    }

    if (!newRule.adultPrice || newRule.adultPrice.trim() === "") {
      newErrors.adultPrice = "Adult price is required";
    } else if (
      isNaN(Number(newRule.adultPrice)) ||
      Number(newRule.adultPrice) < 0
    ) {
      newErrors.adultPrice = "Adult price must be a valid positive number";
    }

    if (!newRule.childPrice || newRule.childPrice.trim() === "") {
      newErrors.childPrice = "Child price is required";
    } else if (
      isNaN(Number(newRule.childPrice)) ||
      Number(newRule.childPrice) < 0
    ) {
      newErrors.childPrice = "Child price must be a valid positive number";
    }

    if (!newRule.infantPrice || newRule.infantPrice.trim() === "") {
      newErrors.infantPrice = "Infant price is required";
    } else if (
      isNaN(Number(newRule.infantPrice)) ||
      Number(newRule.infantPrice) < 0
    ) {
      newErrors.infantPrice = "Infant price must be a valid positive number";
    }

    if (
      newRule.departure.trim() &&
      newRule.destination.trim() &&
      newRule.departure.toUpperCase() === newRule.destination.toUpperCase()
    ) {
      newErrors.destination = "Destination cannot be the same as departure";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkForDuplicates = (): boolean => {
    return checkForDuplicateRbds(newRule, rbdList);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    if (checkForDuplicates()) {
      onDuplicateDetected();
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit();
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold">Add RBD Rule</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Departure *
            </label>
            <input
              type="text"
              value={newRule.departure}
              onChange={(e) =>
                handleInputChange("departure", e.target.value.toUpperCase())
              }
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.departure
                  ? "border-red-300 focus:ring-red-500"
                  : "border-gray-300 focus:ring-blue-500"
              }`}
              maxLength={3}
              placeholder="CMB"
            />
            {errors.departure && (
              <p className="mt-1 text-sm text-red-600">{errors.departure}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Destination *
            </label>
            <input
              type="text"
              value={newRule.destination}
              onChange={(e) =>
                handleInputChange("destination", e.target.value.toUpperCase())
              }
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.destination
                  ? "border-red-300 focus:ring-red-500"
                  : "border-gray-300 focus:ring-blue-500"
              }`}
              maxLength={3}
              placeholder="DXB"
            />
            {errors.destination && (
              <p className="mt-1 text-sm text-red-600">{errors.destination}</p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Airline *
            </label>
            <input
              type="text"
              value={newRule.airLine}
              onChange={(e) =>
                handleInputChange("airLine", e.target.value.toUpperCase())
              }
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.airLine
                  ? "border-red-300 focus:ring-red-500"
                  : "border-gray-300 focus:ring-blue-500"
              }`}
              maxLength={3}
              placeholder="QR"
            />
            {errors.airLine && (
              <p className="mt-1 text-sm text-red-600">{errors.airLine}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              RBD 01 *
            </label>
            <input
              type="text"
              value={newRule.rbd1}
              onChange={(e) =>
                handleInputChange("rbd1", e.target.value.toUpperCase())
              }
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-center ${
                errors.rbd1
                  ? "border-red-300 focus:ring-red-500"
                  : "border-gray-300 focus:ring-blue-500"
              }`}
              maxLength={1}
              placeholder="Y"
            />
            {errors.rbd1 && (
              <p className="mt-1 text-sm text-red-600">{errors.rbd1}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              RBD 02 {!newRule.oneWay && "*"}
            </label>
            <input
              type="text"
              value={newRule.rbd2}
              onChange={(e) =>
                handleInputChange("rbd2", e.target.value.toUpperCase())
              }
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-center ${
                errors.rbd2
                  ? "border-red-300 focus:ring-red-500"
                  : "border-gray-300 focus:ring-blue-500"
              }`}
              maxLength={1}
              disabled={newRule.oneWay}
              placeholder="B"
            />
            {errors.rbd2 && (
              <p className="mt-1 text-sm text-red-600">{errors.rbd2}</p>
            )}
          </div>
        </div>
        <hr className="my-6" />
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Adult Price ({currency}) *
            </label>
            <input
              type="number"
              value={newRule.adultPrice || ""}
              onChange={(e) => handleInputChange("adultPrice", e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.adultPrice
                  ? "border-red-300 focus:ring-red-500"
                  : "border-gray-300 focus:ring-blue-500"
              }`}
              placeholder="0"
              min="0"
              step="0.01"
              required
            />
            {errors.adultPrice && (
              <p className="mt-1 text-sm text-red-600">{errors.adultPrice}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Child Price ({currency}) *
            </label>
            <input
              type="number"
              value={newRule.childPrice || ""}
              onChange={(e) => handleInputChange("childPrice", e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.childPrice
                  ? "border-red-300 focus:ring-red-500"
                  : "border-gray-300 focus:ring-blue-500"
              }`}
              placeholder="0"
              min="0"
              step="0.01"
              required
            />
            {errors.childPrice && (
              <p className="mt-1 text-sm text-red-600">{errors.childPrice}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Infant Price ({currency}) *
            </label>
            <input
              type="number"
              value={newRule.infantPrice || ""}
              onChange={(e) => handleInputChange("infantPrice", e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.infantPrice
                  ? "border-red-300 focus:ring-red-500"
                  : "border-gray-300 focus:ring-blue-500"
              }`}
              placeholder="0"
              min="0"
              step="0.01"
              required
            />
            {errors.infantPrice && (
              <p className="mt-1 text-sm text-red-600">{errors.infantPrice}</p>
            )}
          </div>
        </div>
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Baggage Allowance
          </h4>

          {/* Outbound Baggage */}
          <div className="mb-4">
            <h5 className="text-xs font-medium text-gray-600 mb-2">
              Outbound Baggage
            </h5>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Adult
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={newRule.outboundAdultBaggage}
                    onChange={(e) =>
                      handleInputChange("outboundAdultBaggage", e.target.value)
                    }
                    className="w-full px-3 py-2 pr-16 border rounded-md focus:outline-none focus:ring-2 border-gray-300 focus:ring-blue-500"
                    placeholder={newRule.baggageInKg ? "25" : "1"}
                    min="0"
                    step="1"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <span className="text-gray-500 text-sm">
                      {newRule.baggageInKg ? "Kg" : "Piece(s)"}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Child
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={newRule.outboundChildBaggage}
                    onChange={(e) =>
                      handleInputChange("outboundChildBaggage", e.target.value)
                    }
                    className="w-full px-3 py-2 pr-16 border rounded-md focus:outline-none focus:ring-2 border-gray-300 focus:ring-blue-500"
                    placeholder={newRule.baggageInKg ? "25" : "1"}
                    min="0"
                    step="1"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <span className="text-gray-500 text-sm">
                      {newRule.baggageInKg ? "Kg" : "Piece(s)"}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Infant
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={newRule.outboundInfantBaggage}
                    onChange={(e) =>
                      handleInputChange("outboundInfantBaggage", e.target.value)
                    }
                    className="w-full px-3 py-2 pr-16 border rounded-md focus:outline-none focus:ring-2 border-gray-300 focus:ring-blue-500"
                    placeholder={newRule.baggageInKg ? "10" : "0"}
                    min="0"
                    step="1"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <span className="text-gray-500 text-sm">
                      {newRule.baggageInKg ? "Kg" : "Piece(s)"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Inbound Baggage - Only show for round trip */}
          {!newRule.oneWay && (
            <div>
              <h5 className="text-xs font-medium text-gray-600 mb-2">
                Inbound Baggage
              </h5>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Adult
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={newRule.inboundAdultBaggage}
                      onChange={(e) =>
                        handleInputChange("inboundAdultBaggage", e.target.value)
                      }
                      className="w-full px-3 py-2 pr-16 border rounded-md focus:outline-none focus:ring-2 border-gray-300 focus:ring-blue-500"
                      placeholder={newRule.baggageInKg ? "25" : "1"}
                      min="0"
                      step="1"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <span className="text-gray-500 text-sm">
                        {newRule.baggageInKg ? "Kg" : "Piece(s)"}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Child
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={newRule.inboundChildBaggage}
                      onChange={(e) =>
                        handleInputChange("inboundChildBaggage", e.target.value)
                      }
                      className="w-full px-3 py-2 pr-16 border rounded-md focus:outline-none focus:ring-2 border-gray-300 focus:ring-blue-500"
                      placeholder={newRule.baggageInKg ? "25" : "1"}
                      min="0"
                      step="1"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <span className="text-gray-500 text-sm">
                        {newRule.baggageInKg ? "Kg" : "Piece(s)"}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Infant
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={newRule.inboundInfantBaggage}
                      onChange={(e) =>
                        handleInputChange(
                          "inboundInfantBaggage",
                          e.target.value
                        )
                      }
                      className="w-full px-3 py-2 pr-16 border rounded-md focus:outline-none focus:ring-2 border-gray-300 focus:ring-blue-500"
                      placeholder={newRule.baggageInKg ? "10" : "0"}
                      min="0"
                      step="1"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <span className="text-gray-500 text-sm">
                        {newRule.baggageInKg ? "Kg" : "Piece(s)"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4 mb-6">
          {/* UPDATED: Separate Validity Inputs */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Outbound Validity *
            </label>
            <div className="relative">
              <input
                type="text"
                value={newRule.outboundValidity}
                onClick={() => setActiveCalendarField("outbound")}
                className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 cursor-pointer ${
                  errors.outboundValidity
                    ? "border-red-300 focus:ring-red-500"
                    : "border-gray-300 focus:ring-blue-500"
                }`}
                placeholder="Select outbound dates"
                readOnly
              />
              <Calendar
                className="absolute right-3 top-2.5 text-gray-400 pointer-events-none"
                size={16}
              />
              {/* MOVED INSIDE relative CONTAINER */}
              {activeCalendarField === "outbound" && (
                <CustomCalendar
                  onDateRangeSelect={handleDateRangeSelect}
                  onClose={() => setActiveCalendarField(null)}
                />
              )}
            </div>
            {errors.outboundValidity && (
              <p className="mt-1 text-sm text-red-600">
                {errors.outboundValidity}
              </p>
            )}
          </div>

          <div className="relative">
            <label
              className={`block text-sm font-medium mb-2 ${
                newRule.oneWay ? "text-gray-400" : "text-gray-700"
              }`}
            >
              Inbound Validity {!newRule.oneWay && "*"}
            </label>
            <div className="relative">
              <input
                type="text"
                value={newRule.inboundValidity}
                onClick={() =>
                  !newRule.oneWay && setActiveCalendarField("inbound")
                }
                disabled={newRule.oneWay}
                className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 ${
                  newRule.oneWay
                    ? "bg-gray-100 cursor-not-allowed border-gray-200 text-gray-400"
                    : errors.inboundValidity
                    ? "border-red-300 focus:ring-red-500 cursor-pointer"
                    : "border-gray-300 focus:ring-blue-500 cursor-pointer"
                }`}
                placeholder={
                  newRule.oneWay ? "Not applicable" : "Select inbound dates"
                }
                readOnly
              />
              {!newRule.oneWay && (
                <Calendar
                  className="absolute right-3 top-2.5 text-gray-400 pointer-events-none"
                  size={16}
                />
              )}
              {/* MOVED INSIDE relative CONTAINER */}
              {activeCalendarField === "inbound" && (
                <CustomCalendar
                  onDateRangeSelect={handleDateRangeSelect}
                  onClose={() => setActiveCalendarField(null)}
                />
              )}
            </div>
            {errors.inboundValidity && (
              <p className="mt-1 text-sm text-red-600">
                {errors.inboundValidity}
              </p>
            )}
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Administrating Company
          </label>
          <input
            type="text"
            value={newRule.administratingCompany}
            onChange={(e) =>
              handleInputChange(
                "administratingCompany",
                e.target.value.toUpperCase()
              )
            }
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 border-gray-300 focus:ring-blue-500"
            placeholder="eg: IB,UL"
          />
        </div>

        <div className="flex items-center gap-6 mb-6">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={newRule.oneWay}
              onChange={(e) => {
                handleInputChange("oneWay", e.target.checked);
                if (e.target.checked) handleInputChange("inboundValidity", "");
              }}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">One Way</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={newRule.flexible}
              onChange={(e) => handleInputChange("flexible", e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Flexible</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={newRule.baggageInKg}
              onChange={(e) =>
                handleInputChange("baggageInKg", e.target.checked)
              }
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">
              Baggage in KG
            </span>
          </label>
        </div>
        <div className="flex justify-end">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`px-6 py-2 rounded-lg transition-colors font-medium text-white ${
              isSubmitting
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {isSubmitting ? "Adding..." : "Add RBD Rule"}
          </button>
        </div>
      </div>
    </div>
  );
};

interface PricingModalProps {
  rule: RbdManagementData;
  onSubmit: (pricingData: PricingData) => void;
  onClose: () => void;
}

const PricingModal: React.FC<PricingModalProps> = ({
  rule,
  onSubmit,
  onClose,
}) => {
  const currency = rule.departure.toUpperCase() === "CMB" ? "LKR" : "EUR";

  const isOneWay =
    rule.isOneWay === true ||
    rule.rbd2 === null ||
    rule.rbd2 === undefined ||
    rule.rbd2 === "" ||
    rule.rbd2 === "-";

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [departureSurgeDays, setDepartureSurgeDays] = useState<string[]>(
    rule.departureSurgeDays || []
  );
  const [arrivalSurgeDays, setArrivalSurgeDays] = useState<string[]>(
    isOneWay ? [] : rule.arrivalSurgeDays || []
  );
  const [departureSurgePrices, setDepartureSurgePrices] = useState({
    adult: "",
    child: "",
    infant: "",
  });
  const [arrivalSurgePrices, setArrivalSurgePrices] = useState({
    adult: "",
    child: "",
    infant: "",
  });

  const daysOfWeek = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

  const hasSurgePricing = () => {
    const departureConfigured =
      departureSurgeDays.length > 0 ||
      Number(departureSurgePrices.adult || 0) > 0 ||
      Number(departureSurgePrices.child || 0) > 0 ||
      Number(departureSurgePrices.infant || 0) > 0;

    if (isOneWay) {
      return departureConfigured;
    }

    const arrivalConfigured =
      arrivalSurgeDays.length > 0 ||
      Number(arrivalSurgePrices.adult || 0) > 0 ||
      Number(arrivalSurgePrices.child || 0) > 0 ||
      Number(arrivalSurgePrices.infant || 0) > 0;

    return departureConfigured || arrivalConfigured;
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    const hasDeparturePrices =
      Number(departureSurgePrices.adult || 0) > 0 ||
      Number(departureSurgePrices.child || 0) > 0 ||
      Number(departureSurgePrices.infant || 0) > 0;

    const hasArrivalPrices =
      !isOneWay &&
      (Number(arrivalSurgePrices.adult || 0) > 0 ||
        Number(arrivalSurgePrices.child || 0) > 0 ||
        Number(arrivalSurgePrices.infant || 0) > 0);

    if (hasDeparturePrices && departureSurgeDays.length === 0) {
      newErrors.departureDays =
        "Please select at least one departure day when entering departure prices";
    }

    if (departureSurgeDays.length > 0 && !hasDeparturePrices) {
      newErrors.departurePrices =
        "Please enter at least one departure price when selecting departure days";
    }

    if (!isOneWay) {
      if (hasArrivalPrices && arrivalSurgeDays.length === 0) {
        newErrors.arrivalDays =
          "Please select at least one arrival day when entering arrival prices";
      }

      if (arrivalSurgeDays.length > 0 && !hasArrivalPrices) {
        newErrors.arrivalPrices =
          "Please enter at least one arrival price when selecting arrival days";
      }
    }

    if (
      departureSurgePrices.adult &&
      (isNaN(Number(departureSurgePrices.adult)) ||
        Number(departureSurgePrices.adult) < 0)
    ) {
      newErrors.departureAdultPrice =
        "Departure adult price must be a valid positive number";
    }
    if (
      departureSurgePrices.child &&
      (isNaN(Number(departureSurgePrices.child)) ||
        Number(departureSurgePrices.child) < 0)
    ) {
      newErrors.departureChildPrice =
        "Departure child price must be a valid positive number";
    }
    if (
      departureSurgePrices.infant &&
      (isNaN(Number(departureSurgePrices.infant)) ||
        Number(departureSurgePrices.infant) < 0)
    ) {
      newErrors.departureInfantPrice =
        "Departure infant price must be a valid positive number";
    }

    if (!isOneWay) {
      if (
        arrivalSurgePrices.adult &&
        (isNaN(Number(arrivalSurgePrices.adult)) ||
          Number(arrivalSurgePrices.adult) < 0)
      ) {
        newErrors.arrivalAdultPrice =
          "Arrival adult price must be a valid positive number";
      }
      if (
        arrivalSurgePrices.child &&
        (isNaN(Number(arrivalSurgePrices.child)) ||
          Number(arrivalSurgePrices.child) < 0)
      ) {
        newErrors.arrivalChildPrice =
          "Arrival child price must be a valid positive number";
      }
      if (
        arrivalSurgePrices.infant &&
        (isNaN(Number(arrivalSurgePrices.infant)) ||
          Number(arrivalSurgePrices.infant) < 0)
      ) {
        newErrors.arrivalInfantPrice =
          "Arrival infant price must be a valid positive number";
      }
    }

    if (!hasDeparturePrices && departureSurgeDays.length === 0) {
      if (isOneWay) {
        newErrors.general =
          "Please configure surge pricing for the departure section";
      } else if (!hasArrivalPrices && arrivalSurgeDays.length === 0) {
        newErrors.general =
          "Please configure at least one surge pricing section (departure or arrival)";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearRelatedErrors = (
    section: "departure" | "arrival",
    type: "days" | "prices"
  ) => {
    const errorsToRemove: string[] = [];

    if (section === "departure") {
      if (type === "days") {
        errorsToRemove.push("departureDays", "departurePrices");
      } else {
        errorsToRemove.push("departurePrices", "departureDays");
      }
    } else {
      if (type === "days") {
        errorsToRemove.push("arrivalDays", "arrivalPrices");
      } else {
        errorsToRemove.push("arrivalPrices", "arrivalDays");
      }
    }

    errorsToRemove.push("general");

    setErrors((prev) => {
      const newErrors = { ...prev };
      errorsToRemove.forEach((key) => delete newErrors[key]);
      return newErrors;
    });
  };

  const toggleDepartureDay = (day: string) => {
    setDepartureSurgeDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
    clearRelatedErrors("departure", "days");
  };

  const toggleArrivalDay = (day: string) => {
    if (isOneWay) return;

    setArrivalSurgeDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
    clearRelatedErrors("arrival", "days");
  };

  const handleDepartureSurgePriceChange = (
    type: "adult" | "child" | "infant",
    value: string
  ) => {
    setDepartureSurgePrices((prev) => ({ ...prev, [type]: value }));
    clearRelatedErrors("departure", "prices");
    if (
      errors[`departure${type.charAt(0).toUpperCase() + type.slice(1)}Price`]
    ) {
      setErrors((prev) => ({
        ...prev,
        [`departure${type.charAt(0).toUpperCase() + type.slice(1)}Price`]: "",
      }));
    }
  };

  const handleArrivalSurgePriceChange = (
    type: "adult" | "child" | "infant",
    value: string
  ) => {
    if (isOneWay) return;

    setArrivalSurgePrices((prev) => ({ ...prev, [type]: value }));
    clearRelatedErrors("arrival", "prices");
    if (errors[`arrival${type.charAt(0).toUpperCase() + type.slice(1)}Price`]) {
      setErrors((prev) => ({
        ...prev,
        [`arrival${type.charAt(0).toUpperCase() + type.slice(1)}Price`]: "",
      }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const pricingData: PricingData = {
        departureSurgeDays,
        arrivalSurgeDays: isOneWay ? [] : arrivalSurgeDays,
        departureSurgePrices: {
          adult: parseInt(departureSurgePrices.adult?.toString() || "0") || 0,
          child: parseInt(departureSurgePrices.child?.toString() || "0") || 0,
          infant: parseInt(departureSurgePrices.infant?.toString() || "0") || 0,
        },
        arrivalSurgePrices: isOneWay
          ? { adult: 0, child: 0, infant: 0 }
          : {
              adult: parseInt(arrivalSurgePrices.adult?.toString() || "0") || 0,
              child: parseInt(arrivalSurgePrices.child?.toString() || "0") || 0,
              infant:
                parseInt(arrivalSurgePrices.infant?.toString() || "0") || 0,
            },
      };
      await onSubmit(pricingData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-5xl mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-xl font-semibold">
              {rule.departure} to {rule.destination} -{" "}
              {hasSurgePricing() ? "Edit" : "Add"} Surge Pricing
            </h3>
            {isOneWay && (
              <p className="text-sm text-blue-600 mt-1">
                This is a one-way trip - only departure surge pricing is
                applicable
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        {Object.keys(errors).length > 0 && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600 font-medium">
              Please fix the following errors:
            </p>
            <ul className="mt-1 text-sm text-red-600 list-disc list-inside space-y-1">
              {Object.values(errors).map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="space-y-6">
          {/* Departure to Destination Section - Always show */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-lg font-medium text-gray-800 mb-4 text-left">
              {rule.departure} to {rule.destination}
            </h4>

            <div className="grid grid-cols-2 gap-8">
              <div>
                <h5
                  className={`text-sm font-medium mb-3 ${
                    errors.departureDays ? "text-red-600" : "text-gray-700"
                  }`}
                >
                  Select the Day
                </h5>
                <div className="grid grid-cols-7 gap-2">
                  {daysOfWeek.map((day) => (
                    <div key={day} className="text-center">
                      <div className="text-xs font-medium text-gray-600 mb-2">
                        {day}
                      </div>
                      <label className="cursor-pointer">
                        <input
                          type="checkbox"
                          checked={departureSurgeDays.includes(day)}
                          onChange={() => toggleDepartureDay(day)}
                          className={`w-5 h-5 rounded border-2 text-green-600 focus:ring-green-500 ${
                            errors.departureDays
                              ? "border-red-300"
                              : "border-gray-300"
                          }`}
                        />
                      </label>
                    </div>
                  ))}
                </div>
                {errors.departureDays && (
                  <p className="mt-2 text-sm text-red-600">
                    {errors.departureDays}
                  </p>
                )}
              </div>

              <div>
                <h5
                  className={`text-sm font-medium mb-3 ${
                    errors.departurePrices ? "text-red-600" : "text-gray-700"
                  }`}
                >
                  Price
                </h5>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Adult ({currency})
                    </label>
                    <input
                      type="number"
                      value={departureSurgePrices.adult}
                      onChange={(e) =>
                        handleDepartureSurgePriceChange("adult", e.target.value)
                      }
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 ${
                        errors.departureAdultPrice || errors.departurePrices
                          ? "border-red-300 focus:ring-red-500"
                          : "border-gray-300 focus:ring-green-500"
                      }`}
                      placeholder="0"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Child ({currency})
                    </label>
                    <input
                      type="number"
                      value={departureSurgePrices.child}
                      onChange={(e) =>
                        handleDepartureSurgePriceChange("child", e.target.value)
                      }
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 ${
                        errors.departureChildPrice || errors.departurePrices
                          ? "border-red-300 focus:ring-red-500"
                          : "border-gray-300 focus:ring-green-500"
                      }`}
                      placeholder="0"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Infant ({currency})
                    </label>
                    <input
                      type="number"
                      value={departureSurgePrices.infant}
                      onChange={(e) =>
                        handleDepartureSurgePriceChange(
                          "infant",
                          e.target.value
                        )
                      }
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 ${
                        errors.departureInfantPrice || errors.departurePrices
                          ? "border-red-300 focus:ring-red-500"
                          : "border-gray-300 focus:ring-green-500"
                      }`}
                      placeholder="0"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                {errors.departurePrices && (
                  <p className="mt-2 text-sm text-red-600">
                    {errors.departurePrices}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Only show return section for round trips */}
          {!isOneWay && (
            <>
              <hr className="my-6" />

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-800 mb-4 text-left">
                  {rule.destination} to {rule.departure}
                </h4>

                <div className="grid grid-cols-2 gap-8">
                  <div>
                    <h5
                      className={`text-sm font-medium mb-3 ${
                        errors.arrivalDays ? "text-red-600" : "text-gray-700"
                      }`}
                    >
                      Select the Day
                    </h5>
                    <div className="grid grid-cols-7 gap-2">
                      {daysOfWeek.map((day) => (
                        <div key={day} className="text-center">
                          <div className="text-xs font-medium text-gray-600 mb-2">
                            {day}
                          </div>
                          <label className="cursor-pointer">
                            <input
                              type="checkbox"
                              checked={arrivalSurgeDays.includes(day)}
                              onChange={() => toggleArrivalDay(day)}
                              className={`w-5 h-5 rounded border-2 text-purple-600 focus:ring-purple-500 ${
                                errors.arrivalDays
                                  ? "border-red-300"
                                  : "border-gray-300"
                              }`}
                            />
                          </label>
                        </div>
                      ))}
                    </div>
                    {errors.arrivalDays && (
                      <p className="mt-2 text-sm text-red-600">
                        {errors.arrivalDays}
                      </p>
                    )}
                  </div>

                  <div>
                    <h5
                      className={`text-sm font-medium mb-3 ${
                        errors.arrivalPrices ? "text-red-600" : "text-gray-700"
                      }`}
                    >
                      Price
                    </h5>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          Adult ({currency})
                        </label>
                        <input
                          type="number"
                          value={arrivalSurgePrices.adult}
                          onChange={(e) =>
                            handleArrivalSurgePriceChange(
                              "adult",
                              e.target.value
                            )
                          }
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 ${
                            errors.arrivalAdultPrice || errors.arrivalPrices
                              ? "border-red-300 focus:ring-red-500"
                              : "border-gray-300 focus:ring-purple-500"
                          }`}
                          placeholder="0"
                          min="0"
                          step="0.01"
                        />
                        {errors.arrivalAdultPrice && (
                          <p className="mt-1 text-xs text-red-600">
                            {errors.arrivalAdultPrice}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          Child ({currency})
                        </label>
                        <input
                          type="number"
                          value={arrivalSurgePrices.child}
                          onChange={(e) =>
                            handleArrivalSurgePriceChange(
                              "child",
                              e.target.value
                            )
                          }
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 ${
                            errors.arrivalChildPrice || errors.arrivalPrices
                              ? "border-red-300 focus:ring-red-500"
                              : "border-gray-300 focus:ring-purple-500"
                          }`}
                          placeholder="0"
                          min="0"
                          step="0.01"
                        />
                        {errors.arrivalChildPrice && (
                          <p className="mt-1 text-xs text-red-600">
                            {errors.arrivalChildPrice}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          Infant ({currency})
                        </label>
                        <input
                          type="number"
                          value={arrivalSurgePrices.infant}
                          onChange={(e) =>
                            handleArrivalSurgePriceChange(
                              "infant",
                              e.target.value
                            )
                          }
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 ${
                            errors.arrivalInfantPrice || errors.arrivalPrices
                              ? "border-red-300 focus:ring-red-500"
                              : "border-gray-300 focus:ring-purple-500"
                          }`}
                          placeholder="0"
                          min="0"
                          step="0.01"
                        />
                        {errors.arrivalInfantPrice && (
                          <p className="mt-1 text-xs text-red-600">
                            {errors.arrivalInfantPrice}
                          </p>
                        )}
                      </div>
                    </div>
                    {errors.arrivalPrices && (
                      <p className="mt-2 text-sm text-red-600">
                        {errors.arrivalPrices}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end pt-6 border-t mt-6">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`px-8 py-3 rounded-lg transition-colors font-medium text-white ${
              isSubmitting
                ? "bg-gray-400 cursor-not-allowed"
                : hasSurgePricing()
                ? "bg-yellow-600 hover:bg-yellow-700"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {isSubmitting
              ? "Updating..."
              : hasSurgePricing()
              ? "Update Surge Pricing"
              : "Add Surge Pricing"}
          </button>
        </div>
      </div>
    </div>
  );
};

interface EditPricesModalProps {
  rule: RbdManagementData;
  onClose: () => void;
  onSave: (updatedPrices: Partial<RbdManagementData>) => void;
}

const EditPricesModal: React.FC<EditPricesModalProps> = ({ rule, onClose, onSave }) => {
  const currency = rule.departure.toUpperCase() === "CMB" ? "LKR" : "EUR";
  const hasDepSurge = rule.departureSurgeDays && rule.departureSurgeDays.length > 0;
  const hasArrSurge = rule.arrivalSurgeDays && rule.arrivalSurgeDays.length > 0;

  const [prices, setPrices] = useState({
    adultPrice: rule.adultPrice || "",
    childPrice: rule.childPrice || "",
    infantPrice: rule.infantPrice || "",
    departureSurgeAdultPrice: rule.departureSurgeAdultPrice || "",
    departureSurgeChildPrice: rule.departureSurgeChildPrice || "",
    departureSurgeInfantPrice: rule.departureSurgeInfantPrice || "",
    arrivalSurgeAdultPrice: rule.arrivalSurgeAdultPrice || "",
    arrivalSurgeChildPrice: rule.arrivalSurgeChildPrice || "",
    arrivalSurgeInfantPrice: rule.arrivalSurgeInfantPrice || "",
  });

  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPrices({ ...prices, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    await onSave(prices);
    setIsSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold">
            Edit Prices for {rule.departure} to {rule.destination}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Base Prices */}
          <div>
            <h4 className="font-semibold text-lg border-b pb-2 mb-4">Base Prices ({currency})</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Adult Price</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  name="adultPrice"
                  value={prices.adultPrice}
                  onChange={handleChange}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Child Price</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  name="childPrice"
                  value={prices.childPrice}
                  onChange={handleChange}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Infant Price</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  name="infantPrice"
                  value={prices.infantPrice}
                  onChange={handleChange}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Departure Surge Prices */}
          {hasDepSurge && (
            <div>
              <h4 className="font-semibold text-lg border-b pb-2 mb-4">Departure Surge Prices ({currency})</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Adult Price</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    name="departureSurgeAdultPrice"
                    value={prices.departureSurgeAdultPrice}
                    onChange={handleChange}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Child Price</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    name="departureSurgeChildPrice"
                    value={prices.departureSurgeChildPrice}
                    onChange={handleChange}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Infant Price</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    name="departureSurgeInfantPrice"
                    value={prices.departureSurgeInfantPrice}
                    onChange={handleChange}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Arrival Surge Prices */}
          {hasArrSurge && (
            <div>
              <h4 className="font-semibold text-lg border-b pb-2 mb-4">Arrival Surge Prices ({currency})</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Adult Price</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    name="arrivalSurgeAdultPrice"
                    value={prices.arrivalSurgeAdultPrice}
                    onChange={handleChange}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Child Price</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    name="arrivalSurgeChildPrice"
                    value={prices.arrivalSurgeChildPrice}
                    onChange={handleChange}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Infant Price</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    name="arrivalSurgeInfantPrice"
                    value={prices.arrivalSurgeInfantPrice}
                    onChange={handleChange}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors disabled:opacity-50 flex items-center"
            >
              {isSaving ? (
                <>
                  <Loader2 size={16} className="animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                "Save Prices"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RBDManagement;
